import { Box, Typography, Container, Stack, Chip } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { 
  Business, 
  Public, 
  Storage, 
  Accessibility, 
  Stars, 
  Apartment, 
  Hotel, 
  Language,
  AccountBalance,
  SettingsEthernet,
  Factory,
  TrendingUp,
  PhoneAndroid,
  NetworkCheck,
  PrecisionManufacturing,
  Diversity3,
  Shield,
  Security
} from '@mui/icons-material';

const Timeline = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ['start end', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  const milestones = [
    { 
      year: '1995', 
      title: 'Founded Skyline Estates', 
      description: 'John engineered over three decades of strategic acquisition, renovation/repositioning, and management of 76 Commercial Real Estate buildings including Apartment Rental Buildings, Commercial Rental Buildings, and a North American Hotel & Resort chain.',
      icon: <Business />,
      color: '#C8A96B',
      highlight: true,
      tags: ['Real Estate', '76 Properties', 'Hotels & Resorts']
    },
    { 
      year: '2000', 
      title: 'Telecommunications Infrastructure', 
      description: 'Pioneered investment in telecommunications infrastructure and fiber optic networks across North America, establishing a robust communication backbone for the new millennium and positioning for the digital revolution.',
      icon: <SettingsEthernet />,
      color: '#4FC3F7',
      tags: ['Fiber Optic', 'Network Infrastructure', 'Digital Revolution']
    },
    { 
      year: '2005', 
      title: 'Financial Services & Investment Banking', 
      description: 'Expanded into financial services with strategic investments in commercial banking, asset management, and private equity. Built a comprehensive financial ecosystem to support international operations and investment strategies.',
      icon: <AccountBalance />,
      color: '#66BB6A',
      tags: ['Commercial Banking', 'Asset Management', 'Private Equity']
    },
    { 
      year: '2008', 
      title: 'International Expansion & Diversification', 
      description: 'During the Great Recession caused by Banking Derivative Fraud and the Credit Crisis of 2007/2008, the decision was made to expand and diversify internationally. Building a worldwide business presence by investing specifically in "essential service" Assets worldwide, while shifting away from "disposable income" dependent business/services including Tourism, Hotels & Resorts.',
      icon: <Public />,
      color: '#C8A96B',
      highlight: true,
      tags: ['Global Diversification', 'Essential Services', 'Strategic Pivot']
    },
    { 
      year: '2011', 
      title: 'Refinery & Energy Sector Entry', 
      description: 'Strategic entry into the energy sector with investments in oil refining and petrochemical facilities. Established partnerships with major energy providers and secured essential energy infrastructure assets across multiple jurisdictions.',
      icon: <Factory />,
      color: '#FFA726',
      tags: ['Oil Refining', 'Petrochemicals', 'Energy Infrastructure']
    },
    { 
      year: '2014', 
      title: 'Telecom & Digital Infrastructure Expansion', 
      description: 'Major expansion of telecommunications portfolio including mobile network infrastructure, data centers, and cloud computing services. Investments in 4G/LTE networks and early-stage 5G infrastructure development.',
      icon: <PhoneAndroid />,
      color: '#4FC3F7',
      tags: ['Mobile Networks', 'Data Centers', 'Cloud Computing']
    },
    { 
      year: '2016', 
      title: 'Global Finance & Investment Platform', 
      description: 'Launched a comprehensive global investment platform integrating commercial banking, wealth management, and institutional investment services. Established presence in 30+ countries with focus on emerging markets.',
      icon: <TrendingUp />,
      color: '#66BB6A',
      tags: ['Wealth Management', 'Institutional Investment', 'Emerging Markets']
    },
    { 
      year: '2018', 
      title: 'Direct Access Initiative & Digital Transformation', 
      description: 'Launched the "No Gatekeepers" platform, revolutionizing executive accessibility and transparent communication. Simultaneously invested heavily in digital transformation across all portfolio companies.',
      icon: <Accessibility />,
      color: '#C8A96B',
      highlight: true,
      tags: ['Digital Transformation', 'Executive Accessibility', 'Innovation']
    },
    { 
      year: '2020', 
      title: 'Energy Transition & Sustainability', 
      description: 'Strategic pivot towards sustainable energy solutions while maintaining refinery operations. Investments in renewable energy projects, carbon capture technology, and sustainable refining processes. Positioned portfolio for the green energy transition.',
      icon: <PrecisionManufacturing />,
      color: '#FFA726',
      tags: ['Renewable Energy', 'Carbon Capture', 'Sustainable Refining']
    },
    { 
      year: '2022', 
      title: '5G & Next-Gen Connectivity', 
      description: 'Major investments in 5G network infrastructure, IoT (Internet of Things) platforms, and smart city technologies. Expanded telecommunications portfolio to include satellite communications and edge computing capabilities.',
      icon: <NetworkCheck />,
      color: '#4FC3F7',
      tags: ['5G Networks', 'IoT', 'Smart Cities', 'Edge Computing']
    },
    { 
      year: '2024', 
      title: 'Global Financial Integration', 
      description: 'Launched a unified global financial platform integrating traditional banking with digital assets, blockchain technology, and AI-driven investment strategies. Established partnerships with leading fintech innovators.',
      icon: <Diversity3 />,
      color: '#66BB6A',
      tags: ['Digital Assets', 'Blockchain', 'AI Investment', 'Fintech']
    },
    { 
      year: '2026', 
      title: 'Global Leadership & Next-Gen Vision', 
      description: 'Recognized as a pioneer in executive accessibility, strategic diversification across real estate, finance, energy, and telecommunications. Leading the integration of AI, sustainable energy, and global connectivity with a focus on creating resilient, future-proof infrastructure.',
      icon: <Stars />,
      color: '#C8A96B',
      highlight: true,
      tags: ['AI Integration', 'Sustainable Infrastructure', 'Global Leadership']
    },
  ];

  return (
    <Box 
      ref={targetRef} 
      sx={{ 
        py: { xs: 10, md: 18 }, 
        background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <motion.div style={{ opacity, scale }}>
          <Typography
            variant="subtitle1"
            sx={{ 
              color: '#C8A96B', 
              letterSpacing: 6, 
              mb: 2, 
              textAlign: 'center',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            CAREER MILESTONES • 1995 - 2026
          </Typography>

          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
              fontWeight: 800,
              background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            The Journey Across Industries
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              maxWidth: '700px',
              mx: 'auto',
              color: '#aaa',
              mb: 10,
              fontSize: '1rem',
              lineHeight: 1.6,
            }}
          >
            From commercial real estate to telecommunications, finance, and energy — 
            building a diversified global empire through strategic vision and resilience
          </Typography>
        </motion.div>

        <Stack spacing={5} sx={{ position: 'relative' }}>
          {/* Vertical Timeline Line - Desktop Only */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              height: '90%',
              background: 'linear-gradient(to bottom, #C8A96B, rgba(200,169,107,0.2), #C8A96B)',
              display: { xs: 'none', md: 'block' },
            }}
          />

          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: index * 0.1, ease: "easeOut" }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                  alignItems: 'center',
                  gap: 3,
                  position: 'relative',
                }}
              >
                {/* Timeline Node */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: { md: '50%' },
                    transform: { md: 'translateX(-50%)' },
                    width: { xs: '100%', md: 'auto' },
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 2,
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #111, #1a1a1a)',
                      border: `3px solid ${milestone.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: milestone.color,
                      boxShadow: `0 0 30px ${milestone.color}40`,
                    }}
                  >
                    {milestone.icon}
                  </motion.div>
                </Box>

                {/* Content Box */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '45%' },
                    ml: { md: index % 2 === 0 ? 0 : 'auto' },
                    mr: { md: index % 2 === 0 ? 'auto' : 0 },
                    background: milestone.highlight 
                      ? `linear-gradient(135deg, #111 0%, #1a1610 100%)`
                      : `linear-gradient(135deg, #111 0%, #161616 100%)`,
                    borderRadius: '20px',
                    p: 3.5,
                    border: milestone.highlight 
                      ? '1px solid rgba(200,169,107,0.3)'
                      : '1px solid rgba(200,169,107,0.15)',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': milestone.highlight ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: `linear-gradient(90deg, transparent, ${milestone.color}, transparent)`,
                    } : {},
                    '&:hover': {
                      borderColor: milestone.color,
                      transform: 'translateY(-5px)',
                      boxShadow: `0 10px 30px rgba(0,0,0,0.5)`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Chip
                      label={milestone.year}
                      size="small"
                      sx={{
                        background: `rgba(200,169,107,0.15)`,
                        color: '#C8A96B',
                        fontWeight: 700,
                        letterSpacing: 1,
                        fontSize: '0.85rem',
                      }}
                    />
                    {milestone.highlight && (
                      <Chip
                        label="⭐ Key Milestone"
                        size="small"
                        sx={{
                          background: 'rgba(200,169,107,0.2)',
                          color: '#C8A96B',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Stack>
                  
                  <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700, color: '#fff' }}>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#bbb', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {milestone.description}
                  </Typography>
                  
                  {/* Tags */}
                  {milestone.tags && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${milestone.color}20` }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.8 }}>
                        {milestone.tags.map((tag, i) => (
                          <Chip 
                            key={i}
                            label={tag} 
                            size="small" 
                            sx={{ 
                              background: `${milestone.color}15`, 
                              color: milestone.color,
                              fontSize: '0.7rem',
                              fontWeight: 500,
                            }} 
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </motion.div>
          ))}
        </Stack>

        {/* Bottom Quote Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: 10,
              pt: 5,
              textAlign: 'center',
              borderTop: '1px solid rgba(200,169,107,0.1)',
            }}
          >
            <Typography
              sx={{
                color: '#C8A96B',
                fontStyle: 'italic',
                fontSize: '1.1rem',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              "Crisis creates opportunity. By shifting from disposable income dependency to essential services,
              we built resilience through strategic diversification across real estate, telecommunications, 
              finance, and energy."
            </Typography>
            <Typography
              sx={{
                color: '#777',
                mt: 2,
                fontSize: '0.9rem',
                letterSpacing: 1,
              }}
            >
              — John Asfar, on Strategic Diversification Across Industries
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Timeline;